import { useNavigate } from "react-router-dom";
import {
  generateSubstituteComponentSchedule,
  patchComponentSchedule,
} from "../../helpers/utils/calendar.util";
import { UpdateVacancyDto, Vacancy } from "../../models/vacancy/vacancy";
import Button from "../ui-lib/buttons/Button";
import { useMemo, useState } from "react";
import SubstituteScheduler, {
  SubstituteSchedulerLesson,
} from "../scheduler/SubstituteScheduler";
import { Teacher } from "../../models/teacher/teacher";
import { useVacanciesState } from "../../states/vacancy-state/vacancy.state";
import VacancySubstituteSelectionCombobox from "../../features/vacancy/VacancySubstituteSelectionCombobox";
import { VacancyLesson, isVacancyLesson } from "../../models/schedule/lesson";
import VacancyTeacherOverview from "./VacancyTeacherOverview";
import useSchedulerLessons from "../../hooks/useSchedulerLessons";
import { useManagedState } from "../../hooks/useManagedState";
import { AcceptTeacherDialogResultProps } from "../../features/vacancy/AcceptTeacherApplicationDialog";

type VacancyListDisclosureDetailProps = {
  vacancy: Vacancy;
  preselectedSubstituteTeacher?: Teacher;
};

function VacancyListDisclosureDetail({
  vacancy,
  preselectedSubstituteTeacher,
}: VacancyListDisclosureDetailProps) {
  const [isInEditMode, setIsInEditMode] = useState(false);
  const {
    currentState: substitutes,
    setState: setSubstitutes,
    reset: resetSubstitutes,
  } = useManagedState<Teacher[]>(vacancy.teacherAccepted ?? []);
  const {
    lessons,
    setLessons,
    reset: resetLessons,
  } = useSchedulerLessons(
    generateSubstituteComponentSchedule(
      vacancy.schedule,
      new Date(vacancy.startDate),
      new Date(vacancy.endDate),
      vacancy.lessons,
    ),
  );
  const navigate = useNavigate();
  const { update: updateVacancy } = useVacanciesState();
  const canDirectlyApply = useMemo(
    () =>
      !preselectedSubstituteTeacher ||
      vacancy?.absentTeacher.id === preselectedSubstituteTeacher.id,
    [vacancy, preselectedSubstituteTeacher],
  );
  const substitutesWithPreselected = useMemo(() => {
    const subs = (substitutes ?? []).filter(
      (s) => s.id !== preselectedSubstituteTeacher?.id,
    );
    if (preselectedSubstituteTeacher) {
      subs.push(preselectedSubstituteTeacher);
    }
    return subs;
  }, [preselectedSubstituteTeacher, substitutes]);
  const [highlightedSubstitute, setHighlightedSubstitute] = useState<
    string | undefined
  >(undefined);

  const navigateToVacancy = (id: string) => {
    navigate(`/vacancy/${id}`);
  };

  const onSave = async (
    updatedLessons: SubstituteSchedulerLesson[][],
    updatedSubs: Teacher[],
  ) => {
    const substituteLessons = updatedLessons
      .flatMap((l) => l)
      .filter((l) => l.isActive)
      .filter(isVacancyLesson);
    const update: UpdateVacancyDto = {
      vacancyId: vacancy.id,
      absentTeacherEmail: vacancy.absentTeacher.id,
      absenceReason: vacancy.absenceReason,
      start: vacancy.startDate,
      end: vacancy.endDate,
      description: vacancy.description,
      lessons: (substituteLessons as VacancyLesson[]).map((l) =>
        VacancyLesson.toUpdateVacancyLesson(l),
      ),
    };
    const updatedVacancy = await updateVacancy(
      vacancy.id,
      vacancy.school.id,
      update,
      updatedSubs?.length
        ? {
            substitutes: updatedSubs,
            lessons: substituteLessons,
          }
        : undefined,
    );

    setLessons(
      generateSubstituteComponentSchedule(
        updatedVacancy.schedule,
        new Date(updatedVacancy.startDate),
        new Date(updatedVacancy.endDate),
        updatedVacancy.lessons,
      ),
    );
    setIsInEditMode(false);
  };

  const onAcceptTeacherApplication = async ({
    teacher,
    acceptedLessons,
    declinedLessons,
  }: AcceptTeacherDialogResultProps) => {
    const subs = [...substitutesWithPreselected];
    if (acceptedLessons?.length) {
      subs.push(teacher);
      setSubstitutes(subs);
    }
    const l = patchComponentSchedule(
      lessons,
      lessons
        .flat()
        .filter((l) =>
          [...(acceptedLessons ?? []), ...(declinedLessons ?? [])].some(
            (changed) => VacancyLesson.equals(changed, l as VacancyLesson),
          ),
        )
        .map((l) => {
          l.selectedSubstitute = teacher;
          return l;
        }),
    );
    await onSave(l, subs);
  };

  const onToggleHighlightedTeacher = (teacherId: string) => {
    setHighlightedSubstitute((s) => (s === teacherId ? undefined : teacherId));
  };

  const _setIsInEditMode = (value: boolean) => {
    setIsInEditMode(value);
    if (value) {
      setHighlightedSubstitute(undefined);
    }
  };

  const _setSubstitutes = (substitutes: Teacher[]) => {
    setSubstitutes(substitutes);
  };

  const onCancel = () => {
    resetLessons();
    resetSubstitutes();
    setIsInEditMode(false);
  };

  return (
    <>
      <div className="mt-4 flex justify-end space-x-2">
        {!isInEditMode && (
          <Button onClick={() => _setIsInEditMode(true)}>
            {canDirectlyApply ? "Bearbeiten" : "Bewerben"}
          </Button>
        )}
        {!isInEditMode && (
          <Button onClick={() => navigateToVacancy(vacancy.id)}>Details</Button>
        )}
        {isInEditMode && <Button onClick={onCancel}>Abbrechen</Button>}
        {isInEditMode && (
          <Button
            onClick={() => onSave(lessons, substitutesWithPreselected)}
            theme="success"
          >
            {canDirectlyApply
              ? "Speichern"
              : "Bewerbung abschicken"}
          </Button>
        )}
      </div>
      <hr className="my-4" />
      <div className="flex-grow overflow-auto">
        <div>
          {!isInEditMode && (
            <VacancyTeacherOverview
              vacancy={vacancy}
              onAcceptTeacherApplication={onAcceptTeacherApplication}
              onClick={onToggleHighlightedTeacher}
              activeTeacherCard={highlightedSubstitute}
            />
          )}
          {isInEditMode && !preselectedSubstituteTeacher && (
            <VacancySubstituteSelectionCombobox            
              value={substitutesWithPreselected}
              onSetSubstitutes={(teachers) => _setSubstitutes(teachers ?? [])}
              placeholder="Vertretung suchen ..."
            ></VacancySubstituteSelectionCombobox>
          )}
          <SubstituteScheduler
            substitutes={substitutesWithPreselected}
            lessons={lessons}
            schedule={vacancy.schedule}
            startDate={new Date(vacancy.startDate)}
            teacherMode={preselectedSubstituteTeacher}
            setValue={setLessons}
            isReadOnly={!isInEditMode}
            highlightSubstitute={highlightedSubstitute}
            absentTeacher={vacancy.absentTeacher}
          />
        </div>
      </div>
    </>
  );
}

export default VacancyListDisclosureDetail;
